/**
 * F0-57 — PKCE (RFC 7636) authorisation-code flow against Keycloak.
 *
 * Why this lives in a shared package rather than in the console: the marketing
 * app *starts* the flow (MOD-00 login/signup modals) and the console *completes*
 * it at the callback route. Two copies of PKCE would eventually disagree about
 * the verifier storage key, which fails as a broken login rather than a type
 * error, so it is written once.
 *
 * VERIFIER STORAGE — the one deliberate use of web storage in the codebase.
 * SEC-FR-001 bans *tokens* from localStorage/sessionStorage. The PKCE verifier is
 * not a token: it is single-use, useless without the matching authorisation code,
 * and must survive a full-page redirect to the IdP, which rules out memory. It is
 * kept in sessionStorage, scoped to the tab, and deleted the moment it is
 * consumed. The lint rule is suppressed here and nowhere else.
 */

const VERIFIER_KEY = 'iai-pkce-verifier'
const STATE_KEY = 'iai-pkce-state'
const RETURN_TO_KEY = 'iai-pkce-return-to'

export interface PkceChallenge {
  verifier: string
  challenge: string
  state: string
}

function base64UrlEncode(bytes: Uint8Array) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(byteLength = 32) {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/** S256 challenge. `plain` is not supported — Keycloak accepts S256 and so do we. */
export async function createPkceChallenge(): Promise<PkceChallenge> {
  const verifier = randomString(32)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return {
    verifier,
    challenge: base64UrlEncode(new Uint8Array(digest)),
    state: randomString(16),
  }
}

/* eslint-disable no-restricted-globals -- see the storage note above. */
export function stashPkce(challenge: PkceChallenge, returnTo?: string) {
  sessionStorage.setItem(VERIFIER_KEY, challenge.verifier)
  sessionStorage.setItem(STATE_KEY, challenge.state)
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo)
}

/** Reads and immediately clears the stash — the verifier is single-use. */
export function consumePkce() {
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const state = sessionStorage.getItem(STATE_KEY)
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY)
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)
  return { verifier, state, returnTo }
}
/* eslint-enable no-restricted-globals */

export interface AuthorizeUrlOptions {
  authorizationEndpoint: string
  clientId: string
  redirectUri: string
  challenge: PkceChallenge
  scope?: string
  /** `login` forces re-authentication; used by the "sign in as someone else" path. */
  prompt?: 'login' | 'none' | 'consent'
  /** Pre-fills the IdP form from the marketing modal. */
  loginHint?: string
  /** Routes Keycloak straight to Google/Microsoft (M00-16). */
  identityProviderHint?: 'google' | 'microsoft'
  /** ML-FR-001 — carries the chosen locale into the IdP screens. */
  locale?: string
}

export function buildAuthorizeUrl({
  authorizationEndpoint,
  clientId,
  redirectUri,
  challenge,
  scope = 'openid profile email',
  prompt,
  loginHint,
  identityProviderHint,
  locale,
}: AuthorizeUrlOptions) {
  const url = new URL(authorizationEndpoint)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', clientId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('scope', scope)
  url.searchParams.set('state', challenge.state)
  url.searchParams.set('code_challenge', challenge.challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (prompt) url.searchParams.set('prompt', prompt)
  if (loginHint) url.searchParams.set('login_hint', loginHint)
  if (identityProviderHint) url.searchParams.set('kc_idp_hint', identityProviderHint)
  if (locale) url.searchParams.set('ui_locales', locale)
  return url.toString()
}

export class PkceStateMismatchError extends Error {
  constructor() {
    super('Authorisation state did not match. The sign-in attempt was discarded.')
    this.name = 'PkceStateMismatchError'
  }
}

/**
 * Validates the callback. A state mismatch is treated as a hard failure rather
 * than a retry: it is the CSRF signal this parameter exists for.
 */
export function validateCallback(params: URLSearchParams) {
  const { verifier, state, returnTo } = consumePkce()
  const code = params.get('code')
  const returnedState = params.get('state')
  const error = params.get('error')

  if (error) {
    throw new Error(params.get('error_description') ?? `Sign-in failed: ${error}`)
  }
  if (!code || !verifier) {
    throw new Error('Sign-in could not be completed. Start again from the sign-in page.')
  }
  if (!state || state !== returnedState) {
    throw new PkceStateMismatchError()
  }
  return { code, verifier, returnTo: returnTo ?? undefined }
}
